import { Injectable } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartDataset } from '../_interfaces/analytics.interfaces';
import { CHART_UPDATE_DELAY, CHART_UPDATE_RETRY_DELAY } from '../_constants/analytics.constants';

export interface ChartUpdateOptions {
  chartInstance: BaseChartDirective | undefined;
  chartKey: number;
  labels: string[];
  datasets: ChartDataset[];
  chartOptions?: unknown;
  chartId: string;
  activeTab: string;
  requiredTab: string;
  isDestroyingCharts?: boolean;
  onSuccess?: () => void;
  onRetryExhausted?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ChartUpdateService {
  private chartUpdateRetries: Map<string, number> = new Map();
  private readonly MAX_CHART_UPDATE_RETRIES = 3;

  /**
   * Update a bar chart instance with new data
   */
  updateBarChart(
    cdr: ChangeDetectorRef,
    options: ChartUpdateOptions
  ): void {
    const {
      chartInstance,
      labels,
      datasets,
      chartId,
      activeTab,
      requiredTab,
      isDestroyingCharts = false
    } = options;

    // Only update if we're on the required tab
    if (activeTab !== requiredTab) {
      return;
    }

    // Don't update if charts are being destroyed
    if (isDestroyingCharts) {
      return;
    }

    if (chartInstance && chartInstance.chart) {
      // Reset retry counter on success
      this.chartUpdateRetries.delete(chartId);

      // Update chart data
      if (chartInstance.chart.data) {
        chartInstance.chart.data.labels = labels;
        chartInstance.chart.data.datasets = datasets;
      } else {
        // Create data object if it doesn't exist
        chartInstance.chart.data = {
          labels: labels,
          datasets: datasets
        };
      }

      chartInstance.chart.update('none'); // 'none' = no animation

      if (options.onSuccess) {
        options.onSuccess();
      }
    } else {
      this.handleRetry(cdr, options);
    }
  }

  /**
   * Update a donut chart instance with new data and options
   */
  updateDonutChart(
    cdr: ChangeDetectorRef,
    options: ChartUpdateOptions
  ): void {
    const {
      chartInstance,
      chartKey,
      labels,
      datasets,
      chartOptions,
      chartId,
      activeTab,
      requiredTab,
      isDestroyingCharts = false
    } = options;

    // Only update if we're on the required tab
    if (activeTab !== requiredTab) {
      return;
    }

    // Don't update if chart key is 0 (chart is being destroyed/recreated)
    if (chartKey === 0) {
      return;
    }

    // Don't update if charts are being destroyed
    if (isDestroyingCharts) {
      return;
    }

    if (chartInstance && chartInstance.chart) {
      // Reset retry counter on success
      this.chartUpdateRetries.delete(chartId);

      // Update chart data
      if (chartInstance.chart.data) {
        chartInstance.chart.data.labels = labels;
        chartInstance.chart.data.datasets = datasets;
      } else {
        // Create data object if it doesn't exist
        chartInstance.chart.data = {
          labels: labels,
          datasets: datasets
        };
      }

      // Update chart options if provided
      if (chartOptions) {
        // Chart.js uses chart.config.options internally, so we need to update that
        if (chartInstance.chart.config && chartInstance.chart.config.options) {
          Object.assign(chartInstance.chart.config.options, chartOptions);
          Object.assign(chartInstance.chart.options, chartOptions);
        }
      }

      // Force chart to re-render with new options and data
      chartInstance.chart.update('active');

      // Force re-render to ensure plugin applies radius changes for concentric circles
      if (datasets.length > 1) {
        setTimeout(() => {
          if (chartInstance && chartInstance.chart) {
            chartInstance.chart.render();
          }
        }, 50);
      }

      if (options.onSuccess) {
        options.onSuccess();
      }
    } else {
      this.handleRetry(cdr, options);
    }
  }

  /**
   * Update a line chart instance with new data
   */
  updateLineChart(
    cdr: ChangeDetectorRef,
    options: ChartUpdateOptions
  ): void {
    const {
      chartInstance,
      labels,
      datasets,
      chartId,
      activeTab,
      requiredTab,
      isDestroyingCharts = false
    } = options;

    // Only update if we're on the required tab
    if (activeTab !== requiredTab) {
      return;
    }

    // Don't update if charts are being destroyed
    if (isDestroyingCharts) {
      return;
    }

    if (chartInstance && chartInstance.chart) {
      // Reset retry counter on success
      this.chartUpdateRetries.delete(chartId);

      // Update chart data
      if (chartInstance.chart.data) {
        // Create new arrays to ensure chart detects changes
        chartInstance.chart.data.labels = labels.slice();
        chartInstance.chart.data.datasets = datasets.map(ds => ({ ...ds }));
      } else {
        // If data doesn't exist, create it
        chartInstance.chart.data = {
          labels: labels.slice(),
          datasets: datasets.map(ds => ({ ...ds }))
        };
      }

      // Force a full update to handle label changes
      chartInstance.chart.update('none'); // 'none' = no animation

      // Resize to ensure labels are properly displayed after update
      setTimeout(() => {
        if (chartInstance && chartInstance.chart) {
          chartInstance.chart.resize();
          // Force another update after resize to ensure labels render correctly
          chartInstance.chart.update('none');
        }
      }, 100);

      if (options.onSuccess) {
        options.onSuccess();
      }
    } else {
      this.handleRetry(cdr, options);
    }
  }

  /**
   * Handle retry logic for chart updates
   */
  private handleRetry(
    cdr: ChangeDetectorRef,
    options: ChartUpdateOptions
  ): void {
    const retryCount = this.chartUpdateRetries.get(options.chartId) || 0;

    if (retryCount < this.MAX_CHART_UPDATE_RETRIES) {
      this.chartUpdateRetries.set(options.chartId, retryCount + 1);
      // Retry after a longer delay if chart isn't ready
      setTimeout(() => {
        // Retry the appropriate chart type
        if (options.chartOptions !== undefined) {
          this.updateDonutChart(cdr, options);
        } else if (options.datasets.length > 0 && options.datasets[0].fill !== undefined) {
          this.updateLineChart(cdr, options);
        } else {
          this.updateBarChart(cdr, options);
        }
      }, CHART_UPDATE_RETRY_DELAY);
    } else {
      // Silently stop retrying after max attempts
      this.chartUpdateRetries.delete(options.chartId);
      if (options.onRetryExhausted) {
        options.onRetryExhausted();
      }
    }
  }

  /**
   * Clear retry counter for a specific chart
   */
  clearRetry(chartId: string): void {
    this.chartUpdateRetries.delete(chartId);
  }

  /**
   * Clear all retry counters
   */
  clearAllRetries(): void {
    this.chartUpdateRetries.clear();
  }

  /**
   * Destroy and recreate a donut chart when compare mode or dataset count changes
   */
  destroyAndRecreateDonutChart(
    cdr: ChangeDetectorRef,
    chartInstance: BaseChartDirective | undefined,
    chartKeyRef: { value: number },
    onRecreated: () => void
  ): void {
    // Destroy the existing chart instance
    if (chartInstance && chartInstance.chart) {
      chartInstance.chart.destroy();
      // Clear the chart reference so Angular recreates it
      (chartInstance as any).chart = null;
    }

    // Temporarily set key to 0 to force destruction, then increment to force recreation
    chartKeyRef.value = 0;
    // Use markForCheck to schedule change detection without immediately triggering it
    cdr.markForCheck();
    // Use setTimeout to ensure destruction completes before triggering change detection
    setTimeout(() => {
      // Increment key to recreate chart
      chartKeyRef.value++;
      cdr.markForCheck();
      // Update the chart after recreation
      setTimeout(() => {
        onRecreated();
      }, CHART_UPDATE_DELAY);
    }, 50);
  }

  /**
   * Update chart view with change detection and delayed update
   */
  updateChartView(
    cdr: ChangeDetectorRef,
    updateFn: () => void,
    shouldDefer: boolean = false,
    onDefer?: () => void
  ): void {
    if (shouldDefer && onDefer) {
      onDefer();
      return;
    }

    cdr.detectChanges();

    // Update the chart after a brief delay to ensure it's rendered
    setTimeout(() => {
      updateFn();
    }, CHART_UPDATE_DELAY);
  }

  /**
   * Recreate a bar chart by toggling data arrays
   * This forces Angular to destroy and recreate the chart with new data
   * Useful when the chart instance isn't initialized or when forcing a complete re-render
   * 
   * @param cdr - ChangeDetectorRef for triggering change detection
   * @param getCurrentData - Function that returns current { labels, datasets, compareData }
   * @param setData - Function that sets new { labels, datasets, compareData }
   * @param chartId - Optional chart ID for debugging
   */
  recreateBarChart(
    cdr: ChangeDetectorRef,
    getCurrentData: () => { labels: string[]; datasets: ChartDataset[]; compareData?: number[] },
    setData: (data: { labels: string[]; datasets: ChartDataset[]; compareData: number[] }) => void,
    chartId?: string
  ): void {
    // Store current data
    const currentData = getCurrentData();
    const tempLabels = currentData.labels;
    const tempDatasets = currentData.datasets;
    const tempCompareData = currentData.compareData || [];

    // Clear data to trigger *ngIf="false" and destroy the chart
    setData({ labels: [], datasets: [], compareData: [] });
    cdr.detectChanges();

    // Restore data to trigger *ngIf="true" and recreate the chart
    setTimeout(() => {
      setData({
        labels: tempLabels,
        datasets: tempDatasets,
        compareData: tempCompareData
      });
      cdr.detectChanges();
    }, 10);
  }
}

